import HttpClient from "./httpClient";

export interface MspAnalyticsRequest {
  start_date?: string | null;
  end_date: string;
  client_id?: string | null;
  queue_id?: string | null;
  assignee_id?: string | null;
  /** Status bucket id from `getFilters().statuses` (PSA / ingested label, or "(No status)"). */
  ticket_status?: string | null;
  /** Human labels for saved report title (with client / queue / assignee filters). */
  client_label?: string | null;
  queue_label?: string | null;
  technician_label?: string | null;
  ticket_status_label?: string | null;
  limit?: number;
  annual_ticket_volume?: number;
  technicians?: number;
  model?: string | null;
  batch_size?: number;
  timeframe?: string | null;
  report_name?: string | null;
  /** When true, compute analytics without persisting a research report (e.g. dashboard preview). */
  skip_save_report?: boolean;
}

export interface MspAnalyticsResponse {
  analytics: any;
  saved_report: {
    id: number;
    report_name: string;
    created_at: string;
  } | null;
}

/** Dashboard automatable table: DB-backed daily cache per MSP (`/api/msp-analytics/dashboard-automatable`). */
export type DashboardAutomatableResponse = Omit<MspAnalyticsResponse, "analytics"> & {
  /** When null: cache miss — server serves cache only from the UI (no on-demand compute). */
  analytics: MspAnalyticsResponse["analytics"] | null;
  dashboard_cache?: {
    hit: boolean;
    /** False when nothing cached yet for today (UTC). */
    available?: boolean;
    updated_at?: string | null;
    /** UTC yyyy-mm-dd the client asked for (usually "today"). */
    requested_cache_day?: string;
    /** UTC yyyy-mm-dd of the snapshot actually returned (may be older if today is missing). */
    served_cache_day?: string;
    /** True when ``served_cache_day`` is used because today's row did not exist. */
    prior_day_fallback?: boolean;
    /** Client requested a specific UTC yyyy-mm-dd and no row exists for that day. */
    exact_day_miss?: boolean;
    /** Requested day is not UTC today — data is a dated snapshot only. */
    historical_snapshot?: boolean;
    /** ``force_refresh`` does not recompute past UTC days; past day was loaded read-only or missed. */
    refresh_ignored_for_historical_day?: boolean;
  };
};

export interface TicketAnalysisReportResponse {
  report_id: number | null;
  report_html: string | null;
  report_summary: {
    total?: number;
    alert_count?: number;
    non_alert_count?: number;
    avg_score?: number;
    date_range_label?: string;
    issue_rows?: any[];
    domain_rows?: any[];
    ticket_type_rows?: any[];
    customer_segments?: any[];
    [key: string]: unknown;
  } | null;
  report_metadata: {
    msp_id?: number;
    interval?: string;
    model?: string;
    generated_at?: string;
    date_range_label?: string;
    total_tickets?: number;
    alert_count?: number;
    non_alert_count?: number;
    average_auto_investigation_score?: number;
    [key: string]: unknown;
  } | null;
  dashboard_cache?: {
    hit: boolean;
    available?: boolean;
    updated_at?: string | null;
    requested_cache_day?: string;
    served_cache_day?: string | null;
    prior_day_fallback?: boolean;
    exact_day_miss?: boolean;
    historical_snapshot?: boolean;
    interval?: string;
  };
}

export interface TicketAnalysisSamplesResponse {
  sample_key: string;
  sample_title: string;
  sample_rows: any[];
}

export interface MspAnalyticsFilterOption {
  id: string;
  label: string;
  count: number;
}

export interface MspAnalyticsFiltersResponse {
  clients: MspAnalyticsFilterOption[];
  queues: MspAnalyticsFilterOption[];
  technicians: MspAnalyticsFilterOption[];
  /** Present once Horizon + Cortex expose ticket-status buckets (past ticket ingestion). */
  statuses?: MspAnalyticsFilterOption[];
}

export interface ApiResponse<T> {
  status: "success" | "error";
  data: T;
  timestamp: string;
  error?: string;
}

export interface TicketAnalysisGroup {
  values?: string[]; key: string; label: string; count: number; share_pct: number; details?: Array<{ key: string; label: string; count: number }> }
export interface RecentTicketAnalysis {
  total: number; completed_or_resolved: number; remaining: number; overdue: number; customers: number;
  psa_alert_markers: number;
  groups: Record<string, TicketAnalysisGroup[]>;
}
export interface RecentTicketAiSummary {
  total: number; ready: number; pending: number; failed: number; processing: number; enabled: boolean;
  status: string; model?: string; retry_after_ms?: number; message?: string | null;
  theo_classified: number;
  theo_categories: TicketAnalysisGroup[];
}

export type AutomationBand = 'high' | 'medium' | 'low' | 'insufficient';
export interface TicketAutomationAssessment {
  score?: number | null; effort?: { automated: number | null; manual: number | null }; band: AutomationBand; reason: string; tool_ids: string[]; prerequisites: string[]; company_id?: string; queue_id?: string; category_keys?: string[]; create_date?: string;
}
export interface TicketAutomationSummary {
  days: number; end_at?: string; total: number; ready: number; pending: number; failed: number; disabled?: boolean;
  scope_stats?: Array<{company_id:string;queue_id:string;total:number;ready:number;failed:number}>;
  customer_stats?: Record<string,{ total: number; ready: number; failed: number }>;
  bands: Record<AutomationBand,number>; tickets: Record<string,TicketAutomationAssessment>; assessed_at?: string; partial_scope?: boolean;
}

export interface RequiredTicketIntegration {
  key: string; name: string; access: string[]; reason: string; confidence: 'medium' | 'high';
}
export interface TicketDeviceAnalysis {
  issue_type: string; signal: string; os: string; applications: Array<{name:string;kind:string}>;
  script_feasibility: string; potential_effort: {automated:number|null;manual:number|null}; reason: string;
  scripts: Array<{key:string;name:string;shell:string;purpose:string;prerequisites:string[];verification:string}>;
  blockers: Array<{kind:string;detail:string}>;
}
export interface TicketResolutionAnalysis {
  required_integrations: RequiredTicketIntegration[]; device_analysis: TicketDeviceAnalysis | null;
}
export type CoverageFitBand = AutomationBand | 'pending' | 'failed';
export interface TicketCoverageFilters {
  theo_categories?:string[]; theo_subcategories?:Array<{l1:string;l2:string}>;
  days:number; view:'integrations'|'device'; dimension:string; offset:number; key:string|null; band:CoverageFitBand|null; customer_ids:string; queue_ids:string; include_tickets?:boolean;
}
export interface TicketCoverageResponse {
  supported: boolean; platform_id: number; days: number; total: number; ready: number; pending: number; failed: number; device_total: number;
  groups: Array<{key:string;label:string;count:number;details?:Array<{key:string;label:string;count:number}>;fit:Record<AutomationBand|'pending'|'failed',number>;potential_score:number|null;potential_assessed:number}>;
  ticket_total: number; offset: number;
  tickets: Array<NonNullable<RecentTicketsResponse['tickets']>[number] & {analysis:TicketResolutionAnalysis}>;
}

export interface RecentTicketsResponse {
  platform_id?: number;
  days?: number;
  covered_windows?: RecentTicketsResponse[];
  analysis?: RecentTicketAnalysis;
  ai?: RecentTicketAiSummary;
  partial?: boolean; phase?: string; preview_scope?: {customerIds:string[];queueIds:string[]};
  supported: boolean;
  total?: number;
  tickets?: Array<{ id: number; ticketNumber?: string; title?: string; description?: string;
    customer_name?: string; companyID?: number; createDate?: string; lastActivityDate?: string;
    assignedResourceID?: number; contactID?: number; queueID?: number; assigned_technician_name?: string; requester_name?: string; dueDateTime?: string; completedDate?: string; resolution?: string;
    automation?: TicketAutomationAssessment | null; automation_status?: string | null; labels: Record<string, string | null> }>;
  categories?: Array<{ category: string; count: number }>;
  sync?: { refreshed_at: string | null; start_at: string; end_at: string } | null;
  stale?: boolean;
  refreshing?: boolean;
  warning?: string | null;
}

class MspAnalyticsApi extends HttpClient {
  public exportTicketCsv = async (filters: { days: number; platform_id: number; group: {by: string; key: string};
    customer_ids?: string; queue_ids?: string; ticket_id?: number; origin: string }, signal: AbortSignal, onProgress: (count: number) => void): Promise<Blob> => {
    const chunks: string[] = [];
    let completed = false;
    await this.streamNdjson('/api/msp-analytics/recent-tickets/export', filters, event => {
      if (event.type === 'error') throw new Error(event.data.message || 'Unable to copy ticket data.');
      if (event.type === 'csv') { chunks.push(event.data.text); onProgress(event.data.count); }
      if (event.type === 'done') { completed = true; onProgress(event.data.count); }
    }, signal);
    if (!completed) throw new Error('Ticket copy was interrupted. Please try again.');
    return new Blob(chunks, { type: 'text/plain' });
  };

  public loadTicketCoverage = async (filters: TicketCoverageFilters): Promise<TicketCoverageResponse> => {
    const response: ApiResponse<TicketCoverageResponse> = await this.post('/api/msp-analytics/recent-tickets/coverage', {timeout:60000}, filters);
    return response.data;
  };

  public loadTicketAnalysisPreferences = async (): Promise<{customerIds:string[];queueIds:string[];customers:Array<{key:string;label:string}>;queues:Array<{key:string;label:string}>}> => {
    const response: ApiResponse<any> = await this.get('/api/msp-analytics/recent-tickets/preferences');
    return response.data;
  };

  public loadTicketAnalysisSource = async (): Promise<{ mode: "live" | "cached" }> => {
    const response: ApiResponse<{ mode: "live" | "cached" }> = await this.get('/api/msp-analytics/ticket-analysis-source');
    return response.data;
  };

  public streamTicketAutomation = (days: number, onEvent: (event: { type: string; data: any }) => void, signal: AbortSignal, retry = false): Promise<void> =>
    this.streamNdjson('/api/msp-analytics/recent-tickets/automation/stream', { days, ...(retry?{retry:true}:{}) }, onEvent, signal);

  public loadAutomationDrill = async (days: number, offset: number, filters: { band: string; category?: string; customerIds?: string[]; queueIds?: string[] }): Promise<RecentTicketsResponse> => {
    const response: ApiResponse<RecentTicketsResponse> = await this.post('/api/msp-analytics/recent-tickets/automation/drill', { timeout: 30000 },
      { days, offset, band: filters.band, category: filters.category, customer_ids: filters.customerIds?.join(','), queue_ids: filters.queueIds?.join(',') });
    return response.data;
  };

  public streamRecentTickets = (days: number, dimensions: string[], refresh: boolean, onEvent: (event: { type: string; data: any }) => void, signal: AbortSignal, scope?: {customerIds:string[];queueIds:string[]}): Promise<void> =>
    this.streamNdjson('/api/msp-analytics/recent-tickets/stream', { days, dimensions, refresh, customer_ids:scope?.customerIds.join(','), queue_ids:scope?.queueIds.join(',') }, onEvent, signal);

  public getRecentTicketAnalysis = async (days: number, customerIds: string[] = [], queueIds: string[] = []): Promise<RecentTicketAiSummary> => {
    const response: ApiResponse<RecentTicketAiSummary> = await this.get('/api/msp-analytics/recent-tickets/analyze', { timeout: 30000 }, { days, ...(queueIds.length ? {queue_ids:queueIds.join(',')} : {}), ...(customerIds.length ? { customer_ids: customerIds.join(',') } : {}) });
    return response.data;
  };

  public analyzeRecentTickets = async (days: number, retry = false): Promise<RecentTicketAiSummary> => {
    const response: ApiResponse<RecentTicketAiSummary> = await this.post('/api/msp-analytics/recent-tickets/analyze', { timeout: 90000 }, { days, retry });
    return response.data;
  };

  public loadRecentTickets = async (days: number, offset: number, refresh = false, group?: { by: string; key: string }, options?: { cachedOnly?: boolean; includeTickets?: boolean; dimensions?: string[]; customerIds?: string[]; queueIds?: string[] }): Promise<RecentTicketsResponse> => {
    const response: ApiResponse<RecentTicketsResponse> = await this.get(
      '/api/msp-analytics/recent-tickets', { timeout: 180000 }, { days, offset, ...(refresh ? { refresh: "true" } : {}), ...(group ? { group_by: group.by, group_key: group.key } : {}), ...(options?.cachedOnly ? { cached_only: "true" } : {}), ...(options?.includeTickets === false ? { include_tickets: "false" } : {}), ...(options?.queueIds?.length ? {queue_ids:options.queueIds.join(',')} : {}), ...(options?.customerIds?.length ? { customer_ids: options.customerIds.join(',') } : {}), ...(options?.dimensions?.length ? { group_fields: options.dimensions.join(",") } : {}) });
    return response.data;
  };

  public getFilters = async (params?: {
    start_date?: string | null;
    end_date?: string | null;
    client_id?: string | null;
  }): Promise<MspAnalyticsFiltersResponse> => {
    const response: ApiResponse<MspAnalyticsFiltersResponse> = await this.get(
      `/api/msp-analytics/filters`,
      {},
      {
        ...(params?.start_date ? { start_date: params.start_date } : {}),
        ...(params?.end_date ? { end_date: params.end_date } : {}),
        ...(params?.client_id ? { client_id: params.client_id } : {}),
      }
    );
    return response.data;
  };

  public computeAnalytics = async (request: MspAnalyticsRequest): Promise<MspAnalyticsResponse> => {
    const response: ApiResponse<MspAnalyticsResponse> = await this.post(
      `/api/msp-analytics/compute`,
      { timeout: 600000 },
      request
    );
    return response.data;
  };

  /** Shared MSP + UTC-day cache in Horizon DB (all browsers / users for the MSP). Cache-only from the app. */
  public loadDashboardAutomatable = async (
    opts?: { cache_day?: string | null },
  ): Promise<DashboardAutomatableResponse> => {
    const body: Record<string, unknown> = {};
    if (opts?.cache_day != null && String(opts.cache_day).trim() !== "") {
      body.cache_day = String(opts.cache_day).trim().slice(0, 10);
    }
    const response: ApiResponse<DashboardAutomatableResponse> = await this.post(
      `/api/msp-analytics/dashboard-automatable`,
      { timeout: 120000 },
      Object.keys(body).length ? body : {},
    );
    return response.data;
  };

  /** Dashboard Ticket Analysis tab: authenticated MSP-scoped HTML report cache. */
  public loadTicketAnalysisReport = async (
    opts?: { cache_day?: string | null; interval?: string | null },
  ): Promise<TicketAnalysisReportResponse> => {
    const body: Record<string, unknown> = {};
    if (opts?.cache_day != null && String(opts.cache_day).trim() !== "") {
      body.cache_day = String(opts.cache_day).trim().slice(0, 10);
    }
    if (opts?.interval != null && String(opts.interval).trim() !== "") {
      body.interval = String(opts.interval).trim();
    }
    const response: ApiResponse<TicketAnalysisReportResponse> = await this.post(
      `/api/msp-analytics/ticket-analysis-report`,
      { timeout: 120000 },
      Object.keys(body).length ? body : {},
    );
    return response.data;
  };

  public loadTicketAnalysisSamples = async (
    request: { report_id: number; sample_key: string },
  ): Promise<TicketAnalysisSamplesResponse> => {
    const response: ApiResponse<TicketAnalysisSamplesResponse> = await this.post(
      `/api/msp-analytics/ticket-analysis-report/samples`,
      { timeout: 120000 },
      request,
    );
    return response.data;
  };
}

const mspAnalyticsApi = new MspAnalyticsApi();

export default mspAnalyticsApi;
